/**
 * Phase 7 §2.12 — Profile / Account: single-column sections with per-section Save/Cancel.
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { Camera, Loader2, Trash2, ExternalLink, Shield, Sun, Moon, Monitor } from 'lucide-react';
import { useAuth } from '@core/auth/useAuth';
import { useTheme, type ThemeMode } from '@contexts/ThemeContext';
import { orgService } from '@core/supabase/services/orgService';
import { supabase } from '@core/supabase/client';
import { PrivateStorageAvatar } from '@shared/components/ui/PrivateStorageAvatar';
import { ConfirmDialog } from '@shared/components/ui/ConfirmDialog';
import type { OrgUser } from '@core/types';
import { detailTbBtn, detailTbNeutral, detailTbDanger } from '@shared/lib/detailToolbarStyles';

const LOCALE_STORAGE_KEY = 'app-profile-locale';
const AUTH_PASSWORD_URL = 'https://auth.gograbjob.com/realms/b2b/account/password';

/** Kept for backwards compatibility; profile no longer portals a toolbar here. */
export const SETTINGS_PROFILE_TOOLBAR_HOST_ID = 'settings-profile-primary-toolbar';

async function probeExtendedProfileColumns(orgUserId: string): Promise<boolean> {
  const { error } = await supabase
    .from('ggj_b2b_org_users')
    .select('education')
    .eq('id', orgUserId)
    .maybeSingle();
  if (!error) return true;
  const m = String(error.message ?? '');
  if (error.code === 'PGRST204' || /could not find.*column|schema cache/i.test(m)) return false;
  return false;
}

function nameInitials(name: string | null | undefined): string {
  const parts = (name || '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return 'U';
  return parts.length === 1 ? parts[0][0].toUpperCase() : (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

type ToastKind = 'ok' | 'err';

function SectionShell({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section className="ggj-param-group overflow-hidden rounded-[10px] border vscode-border vscode-bg-secondary/40">
      <div className="ggj-profile-section-h border-b vscode-border px-4 py-3">{title}</div>
      <div className="ggj-profile-section-body space-y-4 p-4">{children}</div>
    </section>
  );
}

export function ProfileSettingsPage({ onAvatarUpdated }: { onAvatarUpdated?: () => void } = {}) {
  const { user } = useAuth();
  const { themeMode, setThemeMode, ianaTimeZone, lockedToLight } = useTheme();
  const avatarInputRef = useRef<HTMLInputElement>(null);

  const [toast, setToast] = useState<{ kind: ToastKind; text: string } | null>(null);
  const pushToast = useCallback((kind: ToastKind, text: string) => {
    setToast({ kind, text });
    window.setTimeout(() => setToast(null), 4200);
  }, []);

  const [orgUser, setOrgUser] = useState<OrgUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [extendedColsOk, setExtendedColsOk] = useState<boolean | null>(null);

  const [identityEditing, setIdentityEditing] = useState(false);
  const [displayName, setDisplayName] = useState('');
  const [phone, setPhone] = useState('');
  const [linkedinUrl, setLinkedinUrl] = useState('');
  const [profileNotes, setProfileNotes] = useState('');
  const [education, setEducation] = useState('');
  const [identitySaving, setIdentitySaving] = useState(false);
  const [identityErr, setIdentityErr] = useState<string | null>(null);

  const [personalEditing, setPersonalEditing] = useState(false);
  const [draftThemeMode, setDraftThemeMode] = useState<ThemeMode>(themeMode);
  const [draftLocale, setDraftLocale] = useState(() => {
    try {
      return localStorage.getItem(LOCALE_STORAGE_KEY) || navigator.language || 'en-US';
    } catch {
      return 'en-US';
    }
  });
  const [personalSaving, setPersonalSaving] = useState(false);

  const [avatarUploading, setAvatarUploading] = useState(false);
  const [avatarError, setAvatarError] = useState<string | null>(null);
  const [avatarImgFailed, setAvatarImgFailed] = useState(false);

  const [confirmSignOutAll, setConfirmSignOutAll] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    setAvatarImgFailed(false);
  }, [orgUser?.avatar_url]);

  useEffect(() => {
    if (!orgUser?.id) {
      setExtendedColsOk(null);
      return;
    }
    let cancelled = false;
    void (async () => {
      const ok = await probeExtendedProfileColumns(orgUser.id);
      if (!cancelled) setExtendedColsOk(ok);
    })();
    return () => {
      cancelled = true;
    };
  }, [orgUser?.id]);

  useEffect(() => {
    if (!user?.orgId) {
      setLoading(false);
      setOrgUser(null);
      return;
    }
    setLoadError(null);
    orgService
      .getTeamMembers(user.orgId)
      .then((members) => {
        const me = members.find((m) => m.keycloak_sub === user.id || m.email === user.email);
        setOrgUser(me ?? null);
        if (me) {
          setDisplayName(me.display_name ?? user.name ?? '');
          setPhone(me.phone ?? '');
          setLinkedinUrl(me.linkedin_url ?? '');
          setProfileNotes(me.profile_notes ?? '');
          setEducation(me.education ?? '');
        } else {
          setDisplayName(user.name ?? '');
          setPhone('');
          setLinkedinUrl('');
          setProfileNotes('');
          setEducation('');
        }
      })
      .catch((e) => {
        setLoadError(e instanceof Error ? e.message : 'Could not load profile.');
      })
      .finally(() => setLoading(false));
  }, [user?.orgId, user?.id, user?.email, user?.name]);

  useEffect(() => {
    if (!personalEditing) {
      setDraftThemeMode(themeMode);
      try {
        setDraftLocale(localStorage.getItem(LOCALE_STORAGE_KEY) || navigator.language || 'en-US');
      } catch {
        setDraftLocale('en-US');
      }
    }
  }, [themeMode, personalEditing]);

  const resetIdentityDraft = () => {
    setDisplayName(orgUser?.display_name ?? user?.name ?? '');
    setPhone(orgUser?.phone ?? '');
    setLinkedinUrl(orgUser?.linkedin_url ?? '');
    setProfileNotes(orgUser?.profile_notes ?? '');
    setEducation(orgUser?.education ?? '');
    setIdentityErr(null);
  };

  const startIdentityEdit = () => {
    setPersonalEditing(false);
    resetIdentityDraft();
    setIdentityEditing(true);
  };

  const startPersonalEdit = () => {
    setIdentityEditing(false);
    setDraftThemeMode(themeMode);
    try {
      setDraftLocale(localStorage.getItem(LOCALE_STORAGE_KEY) || navigator.language || 'en-US');
    } catch {
      setDraftLocale('en-US');
    }
    setPersonalEditing(true);
  };

  const saveIdentity = async () => {
    if (!orgUser) {
      pushToast('err', 'No organisation profile to update.');
      return;
    }
    if (!navigator.onLine) {
      setIdentityErr('You appear to be offline. Connect and try again.');
      pushToast('err', 'Offline — could not save.');
      return;
    }
    const trimmedName = displayName.trim();
    if (!trimmedName) {
      setIdentityErr('Display name is required.');
      return;
    }
    setIdentitySaving(true);
    setIdentityErr(null);
    try {
      const fullPayload = {
        display_name: trimmedName,
        phone: phone.trim() || null,
        linkedin_url: linkedinUrl.trim() || null,
        profile_notes: profileNotes.trim() || null,
        education: education.trim() || null,
      };
      const payload = extendedColsOk === true ? fullPayload : { display_name: trimmedName };
      const { data: updatedRow, error } = await supabase
        .from('ggj_b2b_org_users')
        .update(payload)
        .eq('id', orgUser.id)
        .select()
        .single();
      const msg = error ? String(error.message) : '';
      const isSchemaError =
        error && (error.code === 'PGRST204' || /schema cache|could not find.*column/i.test(msg));
      if (isSchemaError && extendedColsOk === true) {
        setExtendedColsOk(false);
        const { data: row2, error: nameErr } = await supabase
          .from('ggj_b2b_org_users')
          .update({ display_name: trimmedName })
          .eq('id', orgUser.id)
          .select()
          .single();
        if (nameErr) throw nameErr;
        if (row2) setOrgUser(row2 as OrgUser);
        else setOrgUser((prev) => (prev ? { ...prev, display_name: trimmedName } : prev));
        setIdentityEditing(false);
        pushToast('ok', 'Display name saved. Extended fields need a database migration.');
        return;
      }
      if (error) throw error;
      if (updatedRow) setOrgUser(updatedRow as OrgUser);
      else {
        setOrgUser((prev) =>
          prev ? { ...prev, ...(extendedColsOk === true ? fullPayload : { display_name: trimmedName }) } : prev
        );
      }
      setIdentityEditing(false);
      pushToast('ok', 'Profile saved.');
    } catch (err) {
      console.error(err);
      const message =
        err && typeof err === 'object' && 'message' in err
          ? String((err as { message: string }).message)
          : 'Could not save profile.';
      setIdentityErr(message);
      pushToast('err', message);
    } finally {
      setIdentitySaving(false);
    }
  };

  const savePersonal = async () => {
    if (!navigator.onLine) {
      pushToast('err', 'Offline — preferences not saved.');
      return;
    }
    setPersonalSaving(true);
    try {
      try {
        localStorage.setItem(LOCALE_STORAGE_KEY, draftLocale);
      } catch {
        /* ignore */
      }
      setThemeMode(draftThemeMode);
      setPersonalEditing(false);
      pushToast('ok', 'Preferences saved.');
    } catch (e) {
      pushToast('err', e instanceof Error ? e.message : 'Could not save preferences.');
    } finally {
      setPersonalSaving(false);
    }
  };

  const cancelIdentity = () => {
    resetIdentityDraft();
    setIdentityEditing(false);
  };

  const cancelPersonal = () => {
    setDraftThemeMode(themeMode);
    try {
      setDraftLocale(localStorage.getItem(LOCALE_STORAGE_KEY) || navigator.language || 'en-US');
    } catch {
      setDraftLocale('en-US');
    }
    setPersonalEditing(false);
  };

  const handleAvatarFile = async (file: File) => {
    if (!user?.orgId || !orgUser) return;
    if (!file.type.startsWith('image/')) {
      setAvatarError('Please choose an image file (JPEG, PNG, GIF, or WebP).');
      pushToast('err', 'Invalid file type.');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      setAvatarError('Image must be 2MB or smaller.');
      pushToast('err', 'File too large.');
      return;
    }
    setAvatarUploading(true);
    setAvatarError(null);
    setAvatarImgFailed(false);
    try {
      await orgService.uploadMemberAvatar(user.orgId, orgUser.id, file);
      onAvatarUpdated?.();
      const members = await orgService.getTeamMembers(user.orgId);
      const me = members.find((m) => m.keycloak_sub === user.id || m.email === user.email);
      if (me) setOrgUser(me);
      pushToast('ok', 'Photo updated.');
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Could not upload photo.';
      setAvatarError(msg);
      pushToast('err', msg);
    } finally {
      setAvatarUploading(false);
    }
  };

  const handleRemoveAvatar = async () => {
    if (!user?.orgId || !orgUser?.avatar_url) return;
    setAvatarUploading(true);
    setAvatarError(null);
    try {
      await orgService.clearMemberAvatar(user.orgId, orgUser.id);
      onAvatarUpdated?.();
      const members = await orgService.getTeamMembers(user.orgId);
      const me = members.find((m) => m.keycloak_sub === user.id || m.email === user.email);
      if (me) setOrgUser(me);
      pushToast('ok', 'Photo removed.');
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Could not remove photo.';
      setAvatarError(msg);
      pushToast('err', msg);
    } finally {
      setAvatarUploading(false);
    }
  };

  const onConfirmSignOutAll = () => {
    console.warn(
      '[ProfileSettings] TODO: backend contract — POST /auth/sessions/revoke-all (Keycloak admin API or custom) to sign out all devices.',
    );
    setConfirmSignOutAll(false);
    pushToast('ok', 'Sign-out-all is not wired yet — see console for TODO.');
  };

  const onConfirmDelete = () => {
    console.warn(
      '[ProfileSettings] TODO: backend contract — account deletion must revoke org membership, scrub PII, and cascade per GDPR; not implemented in this UI pass.',
    );
    setConfirmDelete(false);
    pushToast('ok', 'Delete account is not wired yet — see console for TODO.');
  };

  const initials = nameInitials(orgUser?.display_name ?? user?.name);
  const retryLoad = () => {
    if (!user?.orgId) return;
    setLoading(true);
    setLoadError(null);
    orgService
      .getTeamMembers(user.orgId)
      .then((members) => {
        const me = members.find((m) => m.keycloak_sub === user.id || m.email === user.email);
        setOrgUser(me ?? null);
      })
      .catch((e) => setLoadError(e instanceof Error ? e.message : 'Could not load profile.'))
      .finally(() => setLoading(false));
  };

  if (loading) {
    return (
      <div className="space-y-4 max-w-2xl" aria-busy="true">
        <div className="h-8 w-48 animate-pulse rounded-[10px] vscode-bg-tertiary" />
        <div className="h-40 animate-pulse rounded-[10px] vscode-bg-tertiary" />
        <div className="h-32 animate-pulse rounded-[10px] vscode-bg-tertiary" />
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="max-w-2xl rounded-[10px] border border-red-500/30 bg-red-500/5 p-4">
        <p className="ggj-text-body vscode-text-primary">{loadError}</p>
        <button type="button" className="btn-secondary mt-3" onClick={retryLoad}>
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="relative max-w-2xl space-y-6 pb-8">
      {/* Phase 8 T-13 §13 B — section header (no PageHeader banner) */}
      <div className="mb-1">
        <h2 className="text-[15px] font-semibold">Profile</h2>
        <p className="text-[12px] vscode-text-tertiary mt-0.5">Manage your personal details and preferences.</p>
      </div>

      {toast ? (
        <div
          className={`fixed bottom-6 right-6 z-[200] max-w-sm rounded-[10px] border px-4 py-3 ggj-text-body  vscode-bg-elevated vscode-border ${
            toast.kind === 'ok' ? 'text-emerald-700 dark:text-emerald-300' : 'text-red-700 dark:text-red-300'
          }`}
          role="status"
        >
          {toast.text}
        </div>
      ) : null}

      {!user?.orgId && (
        <div className="rounded-[10px] border vscode-border px-4 py-3 ggj-text-body vscode-text-secondary">
          You are not linked to an organisation yet. Some profile fields will stay read-only until you join a workspace.
        </div>
      )}

      {/* 1. Identity */}
      <SectionShell title="Identity">
        <input
          ref={avatarInputRef}
          type="file"
          accept="image/jpeg,image/png,image/gif,image/webp"
          className="hidden"
          aria-label="Upload profile photo"
          onChange={(e) => {
            const f = e.target.files?.[0];
            e.target.value = '';
            if (f) void handleAvatarFile(f);
          }}
        />
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start">
          <div className="relative shrink-0">
            <div className="flex h-24 w-24 items-center justify-center overflow-hidden rounded-[10px] border-2 vscode-border vscode-bg-tertiary ggj-text-hero font-semibold vscode-text-primary">
              {orgUser?.avatar_url && !avatarImgFailed ? (
                <PrivateStorageAvatar
                  key={orgUser.avatar_url}
                  path={orgUser.avatar_url}
                  className="h-full w-full object-cover"
                  fallback={<span>{initials}</span>}
                  onImageError={() => setAvatarImgFailed(true)}
                  onSignFailed={() => setAvatarImgFailed(true)}
                />
              ) : (
                initials
              )}
            </div>
            {orgUser && (
              <div className="mt-2 flex flex-wrap gap-2">
                <button
                  type="button"
                  disabled={avatarUploading || !identityEditing}
                  className={`${detailTbBtn} ${detailTbNeutral} ggj-text-meta`}
                  onClick={() => avatarInputRef.current?.click()}
                >
                  {avatarUploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Camera className="h-3.5 w-3.5" />}
                  Upload
                </button>
                {orgUser.avatar_url && !avatarImgFailed ? (
                  <button
                    type="button"
                    disabled={avatarUploading || !identityEditing}
                    className={`${detailTbBtn} ${detailTbDanger} ggj-text-meta`}
                    onClick={() => void handleRemoveAvatar()}
                  >
                    <Trash2 className="h-3.5 w-3.5" /> Remove
                  </button>
                ) : null}
              </div>
            )}
          </div>
          <div className="min-w-0 flex-1 space-y-3">
            <p className="ggj-text-meta vscode-text-tertiary">
              Edit display name and contact fields in this section, then Save. Photo changes apply immediately.
            </p>
            <div>
              <label className="mb-1 block ggj-text-body font-semibold vscode-text-primary" htmlFor="ps-display-name">
                Display name
              </label>
              {identityEditing ? (
                <input
                  id="ps-display-name"
                  className="input w-full ggj-text-body"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  autoComplete="name"
                />
              ) : (
                <p className="ggj-text-body vscode-text-primary">{orgUser?.display_name ?? user?.name ?? '—'}</p>
              )}
            </div>
            <div>
              <p className="mb-1 ggj-text-body font-semibold vscode-text-primary">Email</p>
              <p className="ggj-text-body vscode-text-secondary">{user?.email ?? '—'}</p>
            </div>
            <div>
              <p className="mb-1 ggj-text-body font-semibold vscode-text-primary">Organisation</p>
              <p className="ggj-text-body vscode-text-secondary">{user?.orgName ?? '—'}</p>
            </div>
            {identityEditing && (
              <>
                <div>
                  <label className="mb-1 block ggj-text-body font-semibold vscode-text-primary" htmlFor="ps-phone">
                    Mobile number
                  </label>
                  <input
                    id="ps-phone"
                    className="input w-full ggj-text-body"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    autoComplete="tel"
                  />
                </div>
                <div>
                  <label className="mb-1 block ggj-text-body font-semibold vscode-text-primary" htmlFor="ps-li">
                    LinkedIn URL
                  </label>
                  <input
                    id="ps-li"
                    className="input w-full font-mono ggj-text-body"
                    value={linkedinUrl}
                    onChange={(e) => setLinkedinUrl(e.target.value)}
                  />
                </div>
                <div>
                  <label className="mb-1 block ggj-text-body font-semibold vscode-text-primary" htmlFor="ps-notes">
                    Background / notes
                  </label>
                  <textarea
                    id="ps-notes"
                    className="input min-h-[72px] w-full resize-y ggj-text-body"
                    value={profileNotes}
                    onChange={(e) => setProfileNotes(e.target.value)}
                  />
                </div>
                <div>
                  <label className="mb-1 block ggj-text-body font-semibold vscode-text-primary" htmlFor="ps-edu">
                    Education
                  </label>
                  <textarea
                    id="ps-edu"
                    className="input min-h-[64px] w-full resize-y ggj-text-body"
                    value={education}
                    onChange={(e) => setEducation(e.target.value)}
                  />
                </div>
              </>
            )}
            {extendedColsOk === false && identityEditing && (
              <p className="ggj-text-meta vscode-text-tertiary">
                Extended fields (phone, LinkedIn, notes, education) need{' '}
                <code className="rounded bg-[var(--vscode-bg-secondary)] px-1 font-mono text-[11px]">
                  manual_org_users_profile_fields.sql
                </code>{' '}
                applied — only display name may persist until then.
              </p>
            )}
            {identityErr && <p className="ggj-text-body text-red-500">{identityErr}</p>}
            {avatarError && <p className="ggj-text-meta text-red-500">{avatarError}</p>}
          </div>
        </div>
        <div className="flex flex-wrap gap-2 border-t vscode-border pt-4">
          {identityEditing ? (
            <>
              <button
                type="button"
                className="btn-primary"
                disabled={identitySaving || !orgUser}
                onClick={() => void saveIdentity()}
              >
                {identitySaving ? 'Saving…' : 'Save'}
              </button>
              <button type="button" className="btn-secondary" disabled={identitySaving} onClick={cancelIdentity}>
                Cancel
              </button>
            </>
          ) : (
            <button type="button" className="btn-primary" onClick={startIdentityEdit}>
              Edit identity
            </button>
          )}
        </div>
      </SectionShell>

      {/* 2. Personal details */}
      <SectionShell title="Personal details">
        <p className="ggj-text-meta vscode-text-tertiary">
          Locale is stored in this browser. Theme preference syncs with the rest of the app.
        </p>
        {personalEditing ? (
          <>
            <div>
              <label className="mb-1 block ggj-text-body font-semibold vscode-text-primary" htmlFor="ps-locale">
                Locale
              </label>
              <select
                id="ps-locale"
                className="input w-full max-w-md ggj-text-body"
                value={draftLocale}
                onChange={(e) => setDraftLocale(e.target.value)}
              >
                <option value="en-US">English (US)</option>
                <option value="en-GB">English (UK)</option>
                <option value="en-IN">English (India)</option>
                <option value="en-AU">English (Australia)</option>
              </select>
            </div>
            <div>
              <p className="mb-1 ggj-text-body font-semibold vscode-text-primary">Time zone</p>
              <p className="ggj-text-body vscode-text-secondary">{ianaTimeZone}</p>
              <p className="mt-1 ggj-text-meta vscode-text-tertiary">Detected from your system. Not persisted to the server in this build.</p>
            </div>
            {!lockedToLight && (
            <div>
              <p className="mb-2 ggj-text-body font-semibold vscode-text-primary">Theme</p>
              <div className="flex flex-wrap gap-2">
                {(
                  [
                    ['auto', Monitor, 'Automatic'],
                    ['light', Sun, 'Light'],
                    ['dark', Moon, 'Dark'],
                  ] as const
                ).map(([mode, Icon, label]) => (
                  <button
                    key={mode}
                    type="button"
                    onClick={() => setDraftThemeMode(mode)}
                    className={`inline-flex items-center gap-2 rounded-[10px] border px-3 py-2 ggj-text-body transition-colors ${
                      draftThemeMode === mode
                        ? 'border-[color:var(--ggj-shell-nav-active-border)] vscode-bg-hover vscode-text-primary'
                        : 'vscode-border vscode-text-secondary hover:vscode-bg-hover'
                    }`}
                  >
                    <Icon className="h-4 w-4" aria-hidden />
                    {label}
                  </button>
                ))}
              </div>
            </div>
            )}
          </>
        ) : (
          <dl className="space-y-2 ggj-text-body">
            <div className="flex justify-between gap-4">
              <dt className="vscode-text-tertiary">Locale</dt>
              <dd className="vscode-text-primary">{draftLocale}</dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="vscode-text-tertiary">Time zone</dt>
              <dd className="vscode-text-primary">{ianaTimeZone}</dd>
            </div>
            {!lockedToLight && (
            <div className="flex justify-between gap-4">
              <dt className="vscode-text-tertiary">Theme</dt>
              <dd className="vscode-text-primary capitalize">{themeMode}</dd>
            </div>
            )}
          </dl>
        )}
        <div className="flex flex-wrap gap-2 border-t vscode-border pt-4">
          {personalEditing ? (
            <>
              <button
                type="button"
                className="btn-primary"
                disabled={personalSaving}
                onClick={() => void savePersonal()}
              >
                {personalSaving ? 'Saving…' : 'Save'}
              </button>
              <button type="button" className="btn-secondary" disabled={personalSaving} onClick={cancelPersonal}>
                Cancel
              </button>
            </>
          ) : (
            <button type="button" className="btn-primary" onClick={startPersonalEdit}>
              Edit preferences
            </button>
          )}
        </div>
      </SectionShell>

      {/* 3. Security */}
      <SectionShell title="Security">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="ggj-text-body font-semibold vscode-text-primary">Password</p>
            <p className="ggj-text-meta vscode-text-tertiary">Managed in the account console (Keycloak).</p>
          </div>
          <a
            href={AUTH_PASSWORD_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-secondary inline-flex items-center gap-2 self-start"
          >
            Change password
            <ExternalLink className="h-4 w-4" aria-hidden />
          </a>
        </div>
        <div className="rounded-[10px] border border-dashed vscode-border px-3 py-4">
          <div className="flex items-start gap-2">
            <Shield className="mt-0.5 h-4 w-4 shrink-0 vscode-text-tertiary" aria-hidden />
            <div>
              <p className="ggj-text-body font-semibold vscode-text-primary">Active sessions</p>
              <p className="ggj-text-body vscode-text-secondary">
                {/* TODO: list sessions when backend exposes GET /sessions or Keycloak account API */}
                Session list is not available in this app build. Revoke individual devices from the password console if
                needed.
              </p>
            </div>
          </div>
        </div>
      </SectionShell>

      {/* 4. Danger zone — Phase 8 T-13 §13 C: red text links, not filled buttons */}
      <SectionShell title="Danger zone">
        <p className="ggj-text-body vscode-text-secondary mb-3">
          These actions can affect access to your account. Confirmation is required.
        </p>
        <div className="flex flex-col gap-2">
          <button
            type="button"
            className="text-[13px] text-[#EF4444] hover:underline text-left w-fit"
            onClick={() => setConfirmSignOutAll(true)}
          >
            Sign out of all devices
          </button>
          <button
            type="button"
            className="text-[13px] text-[#EF4444] hover:underline text-left w-fit"
            onClick={() => setConfirmDelete(true)}
          >
            Delete account
          </button>
        </div>
      </SectionShell>

      <ConfirmDialog
        open={confirmSignOutAll}
        onClose={() => setConfirmSignOutAll(false)}
        onConfirm={onConfirmSignOutAll}
        title="Sign out everywhere?"
        message="This would revoke other sessions once the backend endpoint is wired. For now this is a placeholder."
        confirmLabel="Acknowledge"
        variant="primary"
      />
      <ConfirmDialog
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        onConfirm={onConfirmDelete}
        title="Delete account?"
        message="Account deletion is not implemented yet. Confirm to log the intent for a future backend contract."
        confirmLabel="I understand"
        variant="danger"
        requireTypedConfirm="DELETE"
      />
    </div>
  );
}
